import { useLocation, useSearchParams } from "react-router-dom";
import { StyledFieldsStructure } from "./DocumentFieldsStructure.styled"
import { useEffect, useState } from "react";
import { AccountStructureForm, AccountStructureFormSchema } from "../../../../../models/accounts-structure";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, CircularProgress, MenuItem, Select, TextField } from "@mui/material";
import { z } from "zod";
import { ErrorMessage } from "@hookform/error-message";
import { checkInputValue, compareObjects } from "@shared/lib/utils";
import { toast } from 'react-toastify';
import { DocumentStructure, DocumentStructureEdit, DocumentStructureEditSchema } from "../../../../../models/document-structure";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors"
import { getHeaderName, setParams } from "./DocumentFieldsStructure.utils";
import { accountsTables, documentsInputCategories, inputTypes } from "./DocumentFieldsStructure.data";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { parseDocumentSettingsIdToNumber } from "../../add-document/AddDocument.utils";
import { DocumentSettings } from "../../../../../models/document-settings";
import { Table } from "../../../../../models/table";
import { useGetDocumentsSettingsQuery } from "../../../api/documentsSettings.api";
import { useGetSystemTablesQuery } from "@features/tables/api/systemTables.api";
import { useAddCustomDocumentStructureMutation, useEditDocumentStructureMutation, useGetDocumentsStructureQuery } from "@features/structures/api/documentsStructure.api";
import { SelectTable } from "../../../../../models/types";
import { Params } from "./DocumentFieldsStructure.types";



function DocumentFieldsStructure() {

    const location = useLocation();
    const [searchParams] = useSearchParams();

    const { showBoundary } = useErrorBoundary();

    const documentSettingsId: number | null = parseDocumentSettingsIdToNumber(searchParams.get('documentType'));
    
    
    // const documentStructure = useSelector((state: RootState) => state.structure.document);

    const { data: documentsSettings } = useGetDocumentsSettingsQuery();
    const { data: documentsStructure, refetch: refetchDocumentsStructure } = useGetDocumentsStructureQuery();
    const { data: systemTables } = useGetSystemTablesQuery();
    
    const [editDocumentStructure] = useEditDocumentStructureMutation();
    const [addCustomDocumentStructure] = useAddCustomDocumentStructureMutation();
    
    
    const [documentSettings, setDocumentSettings] = useState<DocumentSettings>();
    const [tables, setTables] = useState<SelectTable[]>();
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [documentStructureCopy, setDocumentStructureCopy] = useState<DocumentStructureEdit[]>();

    const params: Params = setParams(location.pathname);
    console.log("params:", params);

    console.log(" DocumentFieldsStructure tables", tables);
    console.log(" DocumentFieldsStructure documentSettings", documentSettings);

    console.log("documentsStructure", documentsStructure);

    // ************************************************************************************************************

    // account structures form

    const accountStructureForm = useForm<{ structures: AccountStructureForm[] }>({
        resolver: zodResolver(z.object({ structures: z.array(AccountStructureFormSchema) })),
        defaultValues: {
            structures: undefined
        }
    });

    const accountStructureFieldsArray = useFieldArray({
        name: 'structures',
        control: accountStructureForm.control
    });


    // ************************************************************************************************************

    // document structures form

    const documentStructureForm = useForm<{ structures: DocumentStructureEdit[] }>({
        resolver: zodResolver(z.object({ structures: z.array(DocumentStructureEditSchema) })),
        defaultValues: {
            structures: documentStructureCopy
        }
    });

    const documentStructureFieldsArray = useFieldArray({
        name: 'structures',
        control: documentStructureForm.control
    });


    // ************************************************************************************************************

    useEffect(()=>{
        if(documentsSettings && documentSettingsId){
            getDocumentsSettingsData(documentsSettings, documentSettingsId);
        }
    },[documentsSettings]);

    
    useEffect(()=>{
        if(systemTables){
            setSystemTablesData(systemTables);
        }
    },[systemTables]);


    useEffect(() => {
        if(documentsStructure){
            setDocumentStructureData();
        }
    }, [documentsStructure]);


    // ************************************************************************************************************

    function getDocumentsSettingsData(documentsSettings: DocumentSettings[], documentSettingsId: number): void {
        try {
            const documentSettings: DocumentSettings | undefined = documentsSettings.find( object => object.id === documentSettingsId );
            setDocumentSettings(documentSettings);
            
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }


    // ************************************************************************************************************


    // set documentStructure state from server or from store.
    // set filtered document structure by documentType and update the state.
    const setDocumentStructureData = async (): Promise<void> => {

        if (documentsStructure) {

            let documentStructureByType: DocumentStructure[] = [];


            console.log("getDocumentStructureData params", params);

            if (params.formType === "browse") {
                const browseTypeOrder = ["מסמך", "תאריך ושעה", "חשבון", "רכב"];

                documentStructureByType = documentsStructure
                    .filter((structure) => browseTypeOrder.includes(structure.type))
                    .sort((a, b) => browseTypeOrder.indexOf(a.type) - browseTypeOrder.indexOf(b.type));
            }

            if (params.documentStructureHebType === "חשבון") {
                documentStructureByType = documentsStructure.filter(structure => structure.type === "חשבון")
            }

            if (params.documentStructureHebType === "רכב") {
                documentStructureByType = documentsStructure.filter(structure => structure.type === "רכב")
            }

            if (params.documentStructureHebType === "עבודה") {
                documentStructureByType = documentsStructure.filter(structure => structure.type === "עבודה")
            }

            if (params.documentStructureHebType === "פריט") {
                documentStructureByType = documentsStructure.filter(structure => structure.type === "פריט")
            }

            if (params.documentStructureHebType === "מסמך") {
                documentStructureByType = documentsStructure.filter(structure => structure.type === "מסמך" && !!structure.custom_field)
            }


            const structureDataForm: DocumentStructureEdit[] = setDocumentStructureShowForm(documentStructureByType, documentSettingsId ?? location.state.document_settings_id);

            setDocumentStructureCopy(z.array(DocumentStructureEditSchema).parse(structureDataForm));
            documentStructureForm.reset({ structures: structureDataForm });
        }
    }


    // ************************************************************************************************************


    // add the property showForm to each structure according to show_form_array
    function setDocumentStructureShowForm(documentStructure: DocumentStructure[], documentSettingsId: number): DocumentStructureEdit[] {

        let documentStructuresWithShowForm: DocumentStructureEdit[];

        documentStructuresWithShowForm = documentStructure.map(structure => {

            // find the show form object of this document.
            const showFormObj = structure.show_form_array?.find(showForm => showForm.document_settings_id === documentSettingsId);
            const structureWithShowForm = { ...structure, show_form: showFormObj ? showFormObj.show_form : false }
            return structureWithShowForm;
        });

        return documentStructuresWithShowForm;
    }


    // ************************************************************************************************************


    // find and update the show form value in the show form array
    function updateStructureShowFormArray(structure: DocumentStructureEdit, documentSettingsId: number) {

        const showFormObj = structure.show_form_array?.find(showForm => showForm.document_settings_id === documentSettingsId);

        if (showFormObj) {
            showFormObj.show_form = structure.show_form!;
        }

        else if (structure.show_form) {
            structure.show_form_array = [{
                document_settings_id: documentSettingsId,
                field_id: structure.id!,
                show_form: true
            }];
        }

    }


    // ************************************************************************************************************


    function setSystemTablesData (systemTables: Table[]): void {
        try {
            const selectSystemTables: SelectTable[] = systemTables.map(table => ({
                value: table.index,
                name: table.name || ""
            }));
            setTables([...selectSystemTables, ...accountsTables]);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }


    // ************************************************************************************************************


    // function handleReturn() {
    //     navigate(getPreviousLink(location.state.breadcrumbData), {
    //         replace: true,
    //         state: {
    //             ...location.state,
    //         }
    //     });
    // }


    // ************************************************************************************************************


    const saveTable = async () => {
        documentStructureForm.handleSubmit(onSubmitDocumentStructure)();
    }


    // ************************************************************************************************************


    const onSubmitDocumentStructure: SubmitHandler<{ structures: DocumentStructureEdit[] }> = async (data) => {

        if (!documentStructureCopy) {
            return;
        }


        try {
            setIsSaving(true);

            const updatedStructuresArray: DocumentStructure[] = [];

            // if structure changed, update the server
            for (const [index, structure] of data.structures.entries()) {
                if (!compareObjects(structure, documentStructureCopy[index])) {

                    updateStructureShowFormArray(structure, documentSettingsId ?? location.state.document_settings_id);

                    const updatedStructure: DocumentStructure = await editDocumentStructure(structure).unwrap();

                    updatedStructuresArray.push(updatedStructure);
                }

                else {
                    updatedStructuresArray.push(structure as DocumentStructure);
                }
            };

            // const structureData = [...documentStructureForm.getValues().structures];

            // for (const [_, structure] of structureData.entries()) {                    
            //     updateStructureShowFormArray(structure, location.state.document_settings_id);
            // };

            toast.success("השינויים נשמרו");
            setDocumentStructureCopy(data.structures);

            // dispatch(setDocumentsStructure(updatedStructuresArray));
            // documentStructureForm.reset({ structure: documentStructureForm.getValues().structure });
            setIsSaving(false);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }

    }


    // ************************************************************************************************************


    const addCustomField = async () => {

        try {
            console.log("addCustomField documentStructureCopy",documentStructureCopy);
            const highestCustomField = (documentStructureCopy ?? []).reduce((max, structure) => {
                const current = structure.custom_field ?? 0;
                return current > max ? current : max;
            }, 0);
            const documentFieldNumber = highestCustomField + 1;

            const documentFieldAdded: DocumentStructure = await addCustomDocumentStructure({

                // document_settings_id: 2,
                type: params.documentStructureHebType ?? "מסמך",
                required: false,
                show_browse: false,
                editable: true,
                field_name: `custom_data.${documentFieldNumber - 1}.field_value`,
                field_heb_name: `מותאם אישית ${documentFieldNumber}`,
                field_display_name: `מותאם אישית ${documentFieldNumber}`,
                field_length: 5,
                field_type: 1,
                field_table: null,
                custom_field: documentFieldNumber,
                location: null,
                to_print: false,
                // show_form_array: []

            }).unwrap();

            const updatedDocumentArray: DocumentStructureEdit[] = [...documentStructureCopy || [], {...documentFieldAdded, show_form: false}]

            // dispatch(setDocumentsStructure([...documentStructureForm.getValues().structure, documentFieldAdded]));
            setDocumentStructureCopy(updatedDocumentArray);
            await refetchDocumentsStructure();
            // documentStructureForm.reset({ structures: [...documentStructureForm.getValues().structures, documentFieldAdded] });

        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
        
    }

    // ************************************************************************************************************



    return (
        <StyledFieldsStructure>
            {!tables ? (<CircularProgress color="inherit" sx={{ display: "block", margin: "auto", padding: 4 }} />) : (
            <>
                <div className="head">
                    {getHeaderName(params, documentSettings)}
                    {/* הגדרת מבנה {params.accountHebType} במסמך {location.state.documentName} */}
                </div>
                <div className="table">
                    <table>
                        <thead>
                            <tr>
                                <th>הצגה {params.formType === 'form' ? "במסמך" : "בטבלה"}</th>
                                <th>שם שדה</th>
                                <th>שם תצוגה</th>
                                <th>קטגוריה</th>
                                <th>אורך</th>
                                <th>סוג</th>
                                <th>טבלה</th>
                                <th>מיקום</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                tables && accountStructureFieldsArray.fields?.map((field, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={params.formType === 'form' ? `structures.${index}.show_form` : `structures.${index}.show_browse`}

                                                render={({ field: { onChange, value } }) => (
                                                    <Checkbox
                                                        disabled={params.formType === 'form' && !!field.required}
                                                        onChange={onChange}
                                                        checked={!!value} />
                                                )}
                                            />
                                        </td>
                                        <td>{field.field_heb_name}</td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_display_name`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_display_name ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_display_name`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_length`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_length ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_length`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_type`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        {inputTypes.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_table`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        <MenuItem value={0}></MenuItem>
                                                        {tables?.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                    </tr>
                                ))
                            }

                            {
                                tables && documentStructureFieldsArray.fields?.map((field, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={params.formType === 'form' ? `structures.${index}.show_form` : `structures.${index}.show_browse`}

                                                render={({ field: { onChange, value } }) => (
                                                    <Checkbox
                                                        disabled={params.formType === 'form' && !!field.required}
                                                        onChange={onChange}
                                                        checked={!!value} />
                                                )}
                                            />
                                        </td>
                                        <td>{field.field_heb_name}</td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.field_display_name`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={documentStructureForm?.formState?.errors?.structures?.[index]?.field_display_name ? true : false}
                                                        helperText={<ErrorMessage errors={documentStructureForm.formState.errors} name={`structures.${index}.field_display_name`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.type`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value, "index")}
                                                        size="small"
                                                        // disabled={!field.custom_field}
                                                        disabled={true}
                                                    >
                                                        {documentsInputCategories.map((obj, index) => (
                                                            <MenuItem key={index} value={obj}>{obj}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.field_length`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={documentStructureForm?.formState?.errors?.structures?.[index]?.field_length ? true : false}
                                                        helperText={<ErrorMessage errors={documentStructureForm.formState.errors} name={`structures.${index}.field_length`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.field_type`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value, "index")}
                                                        size="small"
                                                    >
                                                        {inputTypes.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.field_table`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value, "index")}
                                                        size="small"
                                                    >
                                                        <MenuItem value={""}></MenuItem>
                                                        {tables?.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={documentStructureForm.control}
                                                name={`structures.${index}.location`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={documentStructureForm?.formState?.errors?.structures?.[index]?.location ? true : false}
                                                        helperText={<ErrorMessage errors={documentStructureForm.formState.errors} name={`structures.${index}.location`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
                <div className="buttons">
                    <Button variant='contained' onClick={saveTable} disabled={isSaving}>שמירה</Button>
                    {/* <Button variant="outlined" onClick={handleReturn}>חזור</Button> */}
                    <ReturnButton/>
                    {(params.formType === "form") && (<Button variant="outlined" onClick={addCustomField}>הוספת שורה</Button>)}

                </div>


            </>)}


            {/* <DevTool control={accountStructureForm.control} /> */}

        </StyledFieldsStructure>
    )
}

export default DocumentFieldsStructure